import React, {
  Fragment,
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  KeyboardEvent,
} from "react";
import LoadingLottie from "components/lottieLoading/LoadingLottie";
import { useNavigate, useParams } from "react-router-dom";
import {
  useMessageChatBeforeMutation,
  useMessageChatIndexMutateMutation,
  useSendMessageChatIndexMutation,
  useSwitchChatModeMutation,
  useTrackerLocationLiveQuery,
} from "redux/RTQ/IlenkradApi";
import copy from "copy-to-clipboard";
import { ToastDanger, ToastSuccess } from "utils/toast/Toast";
import CustomCommandModal from "utils/TrackerSettingsModal/CustomCommandModal";
import ArrowRightIcon from "assets/icons/v2/ArrowRightIcon";
import Button from "components/v2/Button/Button";
import { supportAgent, GpsFixed, Verified } from "assets/images";
import classes from "./Chat.module.scss";

interface ChatProps {
  id?: number | string;
  closeDrawer?: () => void;
}

const CloseSupportIcon = () => (
  <svg
    className="support-action-icon"
    width="19"
    height="19"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M20 6L9 17l-5-5"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Helper to extract GPS coordinates from msg.meta or msg.message regex
const extractMessageCoords = (msg: any): { lat: number; lng: number } | null => {
  if (
    msg?.meta?.latitude !== undefined &&
    msg?.meta?.latitude !== null &&
    msg?.meta?.longitude !== undefined &&
    msg?.meta?.longitude !== null
  ) {
    const lat = parseFloat(msg.meta.latitude);
    const lng = parseFloat(msg.meta.longitude);
    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng };
    }
  }

  // Regex check on text: e.g. "lat: 35.123, lng: 51.456" or "35.123456, 51.123456"
  if (typeof msg?.message === "string") {
    const coordMatch = msg.message.match(/([-+]?\d{1,2}\.\d+)\s*,\s*([-+]?\d{1,3}\.\d+)/);
    if (coordMatch) {
      const lat = parseFloat(coordMatch[1]);
      const lng = parseFloat(coordMatch[2]);
      if (!isNaN(lat) && !isNaN(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
        return { lat, lng };
      }
    }
  }

  return null;
};

const Chat: React.FC<ChatProps> = ({ id: propId, closeDrawer }) => {
  const navigate = useNavigate();
  const params = useParams();
  const chatId: any = propId || params?.id;

  const [messageTxt, setMessageTxt] = useState<string>("");
  const [messages, setMessages] = useState<any[]>([]);
  const [chatInfo, setChatInfo] = useState<any>(null);
  const [before_message_id, setBefore_message_id] = useState<any>(null);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isLoadingBefore, setIsLoadingBefore] = useState<boolean>(false);
  const [isCustomCommandModalOpen, setIsCustomCommandModalOpen] =
    useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [isRefreshingAfterSend, setIsRefreshingAfterSend] =
    useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState(() => Date.now());
  const [aiRequestStartedAt, setAiRequestStartedAt] = useState<number | null>(
    null,
  );
  const [isMessageSendTransition, setIsMessageSendTransition] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const prevScrollHeightRef = useRef<number>(0);
  const shouldAnchorScroll = useRef<boolean>(false);
  const isInitialScrollDone = useRef<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { data: dataLocation, isSuccess: isSuccessLocation } =
    useTrackerLocationLiveQuery();

  const [
    mutateChat,
    { data: dataChat, isLoading: isLoadingChat, isSuccess: isSuccessChat },
  ] = useMessageChatIndexMutateMutation();

  const [
    mutateSendMessage,
    { isLoading: isLoadingSendMessage, isSuccess: isSuccessSendMessage },
  ] = useSendMessageChatIndexMutation();

  const [switchChatMode, { isLoading: isSwitchingChatMode }] =
    useSwitchChatModeMutation();

  const [
    mutateBeforeMsg,
    { data: dataBeforeMsg, isSuccess: isSuccessBeforeMsg },
  ] = useMessageChatBeforeMutation();

  const isSupportChat = Boolean(
    chatInfo?.title?.includes("پشتیبانی") ||
    dataChat?.chat?.title?.includes("پشتیبانی") ||
    chatInfo?.type === "support" ||
    dataChat?.chat?.type === "support" ||
    (!chatInfo?.tracker_imei && !dataChat?.chat?.tracker_imei)
  );
  const isSupportChatByType =
    chatInfo?.type === "support" || dataChat?.chat?.type === "support";
  const chatMode = chatInfo?.mode || dataChat?.chat?.mode;

  useEffect(() => {
    if (!isSupportChatByType || chatMode !== "ai_mode") return;

    const interval = window.setInterval(() => setCurrentTime(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [isSupportChatByType, chatMode]);

  // Initial fetch on mount or id change
  useEffect(() => {
    if (chatId) {
      isInitialScrollDone.current = false;
      setHasMore(true);
      setMessages([]);
      mutateChat(chatId);
    }
  }, [chatId]);

  // Handle live tracker online status
  useEffect(() => {
    if (isSuccessLocation && isSuccessChat) {
      const imei = chatInfo?.tracker_imei || dataChat?.chat?.tracker_imei;
      if (imei && dataLocation?.geo_locations) {
        const list = dataLocation.geo_locations;
        if (list.hasOwnProperty(imei)) {
          setIsOnline(list[imei]?.online);
        }
      }
    }
  }, [dataLocation, isSuccessLocation, isSuccessChat, chatInfo, dataChat]);

  // Process dataChat updates (initial load & periodic polling)
  useEffect(() => {
    if (isSuccessChat && dataChat) {
      if (dataChat.chat) {
        setChatInfo(dataChat.chat);
      }
      const incoming = dataChat.messages || [];

      setMessages((prev) => {
        if (prev.length === 0) {
          setBefore_message_id(dataChat.first_message_id);
          return incoming;
        }

        // Merge incoming messages with existing (preserve loaded older history)
        const prevMap = new Map(prev.map((m: any) => [m.id, m]));
        let hasNew = false;
        incoming.forEach((m: any) => {
          if (!prevMap.has(m.id)) {
            hasNew = true;
            prevMap.set(m.id, m);
          } else {
            prevMap.set(m.id, { ...prevMap.get(m.id), ...m });
          }
        });

        const merged = Array.from(prevMap.values()).sort(
          (a: any, b: any) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        );

        // Auto-scroll if user is near the bottom and new message arrived
        if (hasNew && scrollRef.current) {
          const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
          const isNearBottom = scrollHeight - scrollTop - clientHeight < 150;
          if (isNearBottom) {
            setTimeout(() => {
              if (scrollRef.current) {
                scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
              }
            }, 60);
          }
        }
        return merged;
      });
    }
  }, [isSuccessChat, dataChat]);

  // Handle older messages fetched via before_message_id
  useEffect(() => {
    if (isSuccessBeforeMsg && dataBeforeMsg) {
      const olderMessages: any[] = dataBeforeMsg?.messages || [];
      if (olderMessages.length === 0 || !dataBeforeMsg.first_message_id) {
        setHasMore(false);
      } else {
        setBefore_message_id(dataBeforeMsg.first_message_id);
      }

      if (olderMessages.length > 0) {
        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));
          const filteredOlder = olderMessages.filter(
            (m: any) => !existingIds.has(m.id),
          );
          return [...filteredOlder, ...prev];
        });
      }
      setIsLoadingBefore(false);
    }
  }, [isSuccessBeforeMsg, dataBeforeMsg]);

  // Maintain scroll position after older messages are prepended
  useLayoutEffect(() => {
    if (shouldAnchorScroll.current && scrollRef.current) {
      const newScrollHeight = scrollRef.current.scrollHeight;
      const diff = newScrollHeight - prevScrollHeightRef.current;
      scrollRef.current.scrollTop = diff;
      shouldAnchorScroll.current = false;
    }
  }, [messages]);

  // Scroll to bottom on first load
  useEffect(() => {
    if (
      messages.length > 0 &&
      !isInitialScrollDone.current &&
      scrollRef.current
    ) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      isInitialScrollDone.current = true;
    }
  }, [messages]);

  // Periodic polling for new messages (every 4s for support chat) and tab visibility
  useEffect(() => {
    if (!chatId) return;

    const poll = () => {
      setIsRefreshingAfterSend(true);
      mutateChat(chatId).finally(() => setIsRefreshingAfterSend(false));
    };

    const intervalTime = isSupportChat ? 4000 : 8000;
    const interval = setInterval(poll, intervalTime);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        poll();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [chatId, isSupportChat, mutateChat]);

  // Enhanced rapid refresh after sending a message (every 3s for 5 mins)
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    let timeout: NodeJS.Timeout | null = null;

    if (isSuccessSendMessage && isSupportChat && chatId) {
      const refresh = () => {
        setIsRefreshingAfterSend(true);
        mutateChat(chatId).finally(() => setIsRefreshingAfterSend(false));
      };
      refresh();
      interval = setInterval(refresh, 3000);
      timeout = setTimeout(() => {
        if (interval) clearInterval(interval);
      }, 5 * 60 * 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
      if (timeout) clearTimeout(timeout);
    };
  }, [isSuccessSendMessage, chatId, mutateChat, isSupportChat]);

  // Infinite scroll trigger
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    if (
      target.scrollTop < 60 &&
      hasMore &&
      !isLoadingBefore &&
      before_message_id !== null &&
      before_message_id !== undefined
    ) {
      prevScrollHeightRef.current = target.scrollHeight;
      shouldAnchorScroll.current = true;
      setIsLoadingBefore(true);
      mutateBeforeMsg({ id: chatId, before_message_id });
    }
  };

  const handleBack = () => {
    if (closeDrawer) {
      closeDrawer();
    } else if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/contact-us");
    }
  };

  const navigateToMap = (imei: any, longitude: any, latitude: any) => {
    navigate("/notification/chat/map", {
      state: { imei, longitude, latitude, chatId },
    });
  };

  const copyText = (txt: string) => {
    copy(txt);
    ToastSuccess("پیام کپی شد");
  };

  const sendMessage = () => {
    if (!messageTxt.trim() || isLoadingSendMessage || isAiWaiting) return;
    const text = messageTxt.trim();
    setMessageTxt("");
    setIsMessageSendTransition(true);
    window.setTimeout(() => setIsMessageSendTransition(false), 1500);
    if (isSupportChatByType && chatMode === "ai_mode") {
      setAiRequestStartedAt(Date.now());
    }
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
    mutateSendMessage({ id: chatId, message: text });
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    }, 100);
  };

  const handleSupportChatMode = async (
    mode: "mode_closed" | "human_mode",
  ) => {
    if (!chatId || isSwitchingChatMode) return;

    try {
      const result: any = await switchChatMode({ id: chatId, mode }).unwrap();
      const successful = result?.successful ?? result?.data?.successful;

      // A resolved RTK mutation represents a successful 2xx response. Some
      // deployments return an empty body, so only an explicit false is a
      // semantic failure.
      if (successful === false) {
        ToastDanger("تغییر وضعیت گفت‌وگو انجام نشد. دوباره تلاش کنید.");
        return;
      }
    } catch {
      ToastDanger("تغییر وضعیت گفت‌وگو انجام نشد. دوباره تلاش کنید.");
      return;
    }

    setChatInfo((current: any) =>
      current ? { ...current, mode } : { ...(dataChat?.chat || {}), mode },
    );

    try {
      await mutateChat(chatId).unwrap();
    } catch {
      // The mode change succeeded; polling will retry the message refresh.
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleTextareaInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageTxt(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = `${Math.min(e.target.scrollHeight, 100)}px`;
  };

  const chatTitle = isSupportChat
    ? "پشتیبانی"
    : chatInfo?.title || dataChat?.chat?.title || "چت";

  const formatMessageTime = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleTimeString("fa-IR", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    } catch {
      return "";
    }
  };

  const getDayDividerText = (dateStr: string) => {
    try {
      const msgDate = new Date(dateStr);
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);

      if (msgDate.toDateString() === today.toDateString()) {
        return "امروز";
      }
      if (msgDate.toDateString() === yesterday.toDateString()) {
        return "دیروز";
      }
      return msgDate.toLocaleDateString("fa-IR", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "";
    }
  };

  const shouldShowDateDivider = (
    currentMsg: any,
    prevMsg: any | undefined,
  ): boolean => {
    if (!prevMsg) return true;
    const curDay = new Date(currentMsg.created_at).toDateString();
    const prevDay = new Date(prevMsg.created_at).toDateString();
    return curDay !== prevDay;
  };

  const isMyMessage = (message: any) => {
    // Only messages explicitly sent by the user (user_support) are "my messages" on the right.
    // Tracker alerts, tracker status updates, command responses, and admin/support messages are received messages on the left.
    if (
      message.message_type === "tracker_alert" ||
      message.message_type === "command_response" ||
      message.message_type === "admin_support" ||
      message.message_type === "ai_bot_support" ||
      chatInfo?.tracker_imei
    ) {
      return false;
    }

    return (
      message.message_type === "user_support" ||
      (message.user_id &&
        chatInfo?.user_id &&
        message.user_id === chatInfo.user_id &&
        message.message_type !== "admin_support" &&
        message.message_type !== "ai_bot_support")
    );
  };

  const latestMessage = messages.reduce(
    (latest: any, message: any) =>
      !latest ||
      new Date(message.created_at).getTime() >
        new Date(latest.created_at).getTime()
        ? message
        : latest,
    null,
  );
  const latestMessageIsUser = Boolean(latestMessage && isMyMessage(latestMessage));
  const latestMessageAge = latestMessage?.created_at
    ? currentTime - new Date(latestMessage.created_at).getTime()
    : Infinity;
  const hasRecentUserMessage =
    latestMessageIsUser && latestMessageAge >= 0 && latestMessageAge < 2 * 60 * 1000;
  const hasPendingAiRequest =
    aiRequestStartedAt !== null &&
    currentTime - aiRequestStartedAt < 2 * 60 * 1000;
  const isAiWaiting =
    Boolean(isSupportChatByType) &&
    chatMode === "ai_mode" &&
    (hasRecentUserMessage || hasPendingAiRequest);
  const isAiTyping =
    isAiWaiting;

  useEffect(() => {
    if (
      aiRequestStartedAt !== null &&
      latestMessage &&
      !latestMessageIsUser &&
      new Date(latestMessage.created_at).getTime() >= aiRequestStartedAt
    ) {
      setAiRequestStartedAt(null);
    }
  }, [aiRequestStartedAt, latestMessage?.id, latestMessage?.created_at, latestMessageIsUser]);

  useEffect(() => {
    if (!isAiTyping || !scrollRef.current) return;

    window.requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    });
  }, [isAiTyping, messages.length]);

  if (isLoadingChat && messages.length === 0 && !isRefreshingAfterSend) {
    return <LoadingLottie />;
  }

  const renderedMessages = messages;

  return (
    <div className={classes.chatContainer}>
      <CustomCommandModal
        imei={chatInfo?.tracker_imei || dataChat?.chat?.tracker_imei}
        command=""
        statusCommand=""
        sendSms={() => {}}
        onClose={() => setIsCustomCommandModalOpen(false)}
        open={isCustomCommandModalOpen}
        online={isOnline}
      />

      {/* Top App Header */}
      <header className={classes.chatHeader}>
        <div className={classes.headerRight}>
          <div
            className={classes.backButton}
            onClick={handleBack}
            role="button"
            tabIndex={0}
            aria-label="بازگشت"
            title="بازگشت"
          >
            <ArrowRightIcon />
          </div>

          <div className={classes.avatarWrapper}>
            <img
              src={
                isSupportChat
                  ? supportAgent
                  : chatInfo?.tracker_imei || dataChat?.chat?.tracker_imei
                  ? GpsFixed
                  : Verified
              }
              alt={chatTitle}
              className={classes.avatarImg}
            />
          </div>

          <div className={classes.headerMeta}>
            <h1 className={classes.chatTitle}>{chatTitle}</h1>
          </div>
        </div>

        <div className={classes.headerLeft}>
          {closeDrawer && (
            <button
              type="button"
              className={classes.backButton}
              onClick={closeDrawer}
              aria-label="بستن"
              title="بستن"
            >
              <div className="icon3-close" />
            </button>
          )}
        </div>
      </header>

      {/* Messages Scroll Area with Infinite Scroll */}
      <div
        className={classes.chatMessages}
        ref={scrollRef}
        onScroll={handleScroll}
      >
        {/* Loading indicator when fetching older messages */}
        {isLoadingBefore && (
          <div className={classes.topLoadingContainer}>
            <div className={classes.loadingPill}>
              <div className={classes.spinner} />
              <span>در حال بارگذاری پیام‌های قبل...</span>
            </div>
          </div>
        )}

        {renderedMessages.length === 0 && (
          <div className={classes.emptyState}>
            <div>در حال حاضر پیامی برای نمایش وجود ندارد.</div>
          </div>
        )}

        {renderedMessages.map((msg: any, index: number) => {
          const showDivider = shouldShowDateDivider(
            msg,
            renderedMessages[index - 1],
          );
          const isMine = isMyMessage(msg);
          const coords = extractMessageCoords(msg);

          return (
            <Fragment key={msg.id || index}>
              {showDivider && (
                <div className={classes.dateDivider}>
                  <span className={classes.dateChip}>
                    {getDayDividerText(msg.created_at)}
                  </span>
                </div>
              )}

              <div
                className={`${classes.messageRow} ${
                  isMine ? classes.sentRow : classes.receivedRow
                }`}
              >
                <div
                  className={`${classes.bubble} ${
                    isMine ? classes.sentBubble : classes.receivedBubble
                  }`}
                >
                  {!isMine && (isSupportChat || msg.message_type === "ai_bot_support") && (
                    <span className={classes.senderName}>
                      {msg.message_type === "ai_bot_support"
                        ? "ربات هوشمند"
                        : "کارشناس پشتیبانی"}
                    </span>
                  )}

                  <div className={classes.messageText}>{msg.message}</div>

                  <div className={classes.messageFooter}>
                    <div className={classes.footerActions}>
                      <button
                        type="button"
                        className={classes.bubbleActionIconBtn}
                        onClick={() => copyText(msg.message)}
                        title="کپی پیام"
                        aria-label="کپی پیام"
                      >
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z"
                            fill="currentColor"
                          />
                        </svg>
                      </button>

                      {coords && (
                        <button
                          type="button"
                          className={classes.bubbleActionIconBtn}
                          onClick={() =>
                            navigateToMap(
                              chatInfo?.tracker_imei,
                              coords.lng,
                              coords.lat,
                            )
                          }
                          title="مشاهده موقعیت روی نقشه"
                          aria-label="مشاهده موقعیت روی نقشه"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z"
                              fill="#FF5C3D"
                            />
                          </svg>
                        </button>
                      )}
                    </div>

                    <div className={classes.footerMeta}>
                      <span>{formatMessageTime(msg.created_at)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Fragment>
          );
        })}

        {isAiTyping && (
          <div className={classes.aiTypingIndicator} role="status" aria-live="polite">
            <span>پشتیبان هوشمند درحال بررسی درخواست شماست</span>
            <span className={classes.typingDots} aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
          </div>
        )}
      </div>

      {/* WhatsApp / Telegram Bottom Input Bar */}
      <footer className={classes.chatInputBar}>
        {isSupportChatByType &&
          (chatMode === "ai_mode" || chatMode === "human_mode") &&
          !isAiWaiting &&
          !isMessageSendTransition &&
          !isLoadingSendMessage &&
          !isSwitchingChatMode && (
          <div className={classes.supportActions}>
            <Button
              type="primary"
              text="مشکلم حل شد"
              icon={<CloseSupportIcon />}
              onClick={() => handleSupportChatMode("mode_closed")}
              disabled={isSwitchingChatMode}
              additionalStyles={classes.supportActionButton}
            />
            {chatMode !== "human_mode" && !hasRecentUserMessage && (
              <Button
                type="basic"
                text="ارجاع به کارشناس"
                icon={
                  <img
                    src={supportAgent}
                    alt=""
                    className={classes.supportActionIcon}
                  />
                }
                onClick={() => handleSupportChatMode("human_mode")}
                disabled={isSwitchingChatMode}
                additionalStyles={classes.supportActionButton}
              />
            )}
          </div>
        )}

        {chatInfo?.tracker_imei ? (
          <Button
            type="basic"
            text="ارسال دستور به ردیاب"
            icon={<div className={`icon5-maps_ugc ${classes.customCommandIcon}`} />}
            onClick={() => setIsCustomCommandModalOpen(true)}
            additionalStyles={classes.customCommandBtn}
          />
        ) : (
          <>
            <div className={classes.inputFieldWrapper}>
              <textarea
                ref={textareaRef}
                className={classes.chatTextarea}
                rows={1}
                placeholder="پیام خود را بنویسید..."
                value={messageTxt}
                onChange={handleTextareaInput}
                onKeyDown={handleKeyDown}
                disabled={isAiWaiting}
                aria-label="متن پیام"
              />
            </div>

            <button
              type="button"
              className={classes.sendButton}
              onClick={sendMessage}
              disabled={!messageTxt.trim() || isLoadingSendMessage || isAiWaiting}
              aria-label="ارسال پیام"
              title="ارسال"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={classes.sendIconSvg}
              >
                {/* Send arrow pointing to the left (RTL send direction) */}
                <path
                  d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z"
                  fill="currentColor"
                  transform="rotate(180 12 12)"
                />
              </svg>
            </button>
          </>
        )}
      </footer>
    </div>
  );
};

export default Chat;
